import React from 'react';
import {
  Autocomplete,
  Box,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ParlayLeg, ProbUncertaintyBand, RaceChaos, SimDriverResult, SimModel } from '../services/api';
import { BetValuePanel } from './BetValuePanel';
import { addToSlip, betFromLegs, useSlipCount } from '../utils/slipStore';
import { SegmentedToggle } from './SegmentedToggle';
import { ParlaySlip } from './ParlaySlip';
import { TracksUsedPanel, TracksUsedPanelProps } from './TracksUsedPanel';
import { bandFor, marketForTopN } from '../utils/betValue';
import { bookMarketForTopN } from '../utils/bookMode';
import { alpha, lighten } from '@mui/material/styles';

export type BetTab = 'single' | 'parlay' | 'tracks';
export interface SingleBetSelection {
  driver: string;
  /** 1 = win, otherwise top N. */
  n: number;
}

const MARKETS = [
  { n: 1, label: 'Win' },
  { n: 3, label: 'Top 3' },
  { n: 5, label: 'Top 5' },
  { n: 10, label: 'Top 10' },
];

const probFor = (d: SimDriverResult, n: number) =>
  n === 1 ? d.winProb : n === 3 ? d.top3Prob : n === 5 ? d.top5Prob : d.top10Prob;

interface BetToolsCardProps {
  tab: BetTab;
  onTabChange: (tab: BetTab) => void;
  /** Drivers as shown in the table (follows the track-specific layer switch). */
  drivers: SimDriverResult[];
  single: SingleBetSelection | null;
  onSingleChange: (s: SingleBetSelection | null) => void;
  uncertainty?: ProbUncertaintyBand[];
  // Parlay builder
  site: string;
  excludeTracks: string[];
  legs: ParlayLeg[];
  onLegsChange: (legs: ParlayLeg[]) => void;
  showOdds: boolean;
  model: SimModel;
  /** The run's race chaos scenario (parlays are priced the same way). */
  chaos?: RaceChaos;
  // Tracks feeding the simulation (third tab).
  tracksPanel: Omit<TracksUsedPanelProps, 'embedded'>;
  /** Track Specialists breakdown, shown above the tracks list on that tab (when the layer is active). */
  layerPanel?: React.ReactNode;
}

/** Sidebar card with the single-bet value calculator and the parlay builder as tabs. */
export const BetToolsCard: React.FC<BetToolsCardProps> = ({
  tab,
  onTabChange,
  drivers,
  single,
  onSingleChange,
  uncertainty,
  site,
  excludeTracks,
  legs,
  onLegsChange,
  showOdds,
  model,
  chaos,
  tracksPanel,
  layerPanel,
}) => {
  const slipCount = useSlipCount(site);
  const byRank = [...drivers].sort((a, b) => a.projectedRank - b.projectedRank);
  const selected = single ? drivers.find((d) => d.driver === single.driver) : undefined;
  const prob = selected && single ? probFor(selected, single.n) : null;
  const theme = useTheme();
  // Same bright tone as the main results table's rows, so the header (plain paper) and body (below) read
  // as distinct surfaces against it, the same three-tier relationship the table itself uses.
  const cardBg = theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.05) : theme.palette.background.paper;
  // Half-opacity over that bright ambient lands between it and the header's paper tone — the same blend the
  // table's unrolled driver rows use, so the sidebar body isn't as bright as the table rows above it.
  const bodyBg = alpha(
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default,
    0.5
  );

  return (
    <Paper sx={{ p: 0, bgcolor: cardBg }}>
      <Box sx={{ px: 2, pt: 0.5, pb: 1, bgcolor: 'background.paper', borderTopLeftRadius: 12, borderTopRightRadius: 12, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, v: BetTab) => onTabChange(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, fontWeight: 600, fontSize: 13, minWidth: 0 },
          }}
        >
          <Tab value="single" label="Single bet" />
          <Tab value="parlay" label={`Parlay${legs.length > 0 ? ` (${legs.length})` : ''}`} />
          <Tab value="tracks" label={tracksPanel.twoLayer ? 'Form' : 'Tracks'} />
        </Tabs>
      </Box>

      <Box sx={{ bgcolor: bodyBg, p: 2, borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
      {tab === 'tracks' ? (
        <>
          {layerPanel}
          <TracksUsedPanel embedded {...tracksPanel} />
        </>
      ) : tab === 'single' ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Click any Win / Top 3 / Top 5 / Top 10 number in the table, or pick one here, then enter the book&apos;s
            odds to see whether it&apos;s worth a bet.
          </Typography>
          <Autocomplete
            size="small"
            options={byRank.map((d) => d.driver)}
            value={single?.driver ?? null}
            onChange={(_, v) => onSingleChange(v ? { driver: v, n: single?.n ?? 1 } : null)}
            renderInput={(params) => <TextField {...params} label="Driver" />}
          />
          <Box sx={{ mt: 1.5, mb: 2 }}>
            <SegmentedToggle
              aria-label="Market"
              fullWidth
              size="small"
              disabled={!single}
              value={String(single?.n ?? 1)}
              onChange={(v) => single && onSingleChange({ ...single, n: Number(v) })}
              options={MARKETS.map((m) => ({ value: String(m.n), label: m.label }))}
            />
          </Box>
          {single && prob !== null && (
            <BetValuePanel
              key={`${single.driver}|${single.n}`}
              prob={prob}
              bandPts={bandFor(uncertainty, marketForTopN(single.n), prob)}
              bookMarket={bookMarketForTopN(single.n)}
              onAddToSlip={(oddsText, stakeText) => {
                const bet = betFromLegs([{ kind: 'top_n', driver: single.driver, n: single.n }], oddsText, stakeText);
                return !!bet && addToSlip(site, bet);
              }}
              slipCount={slipCount}
            />
          )}
        </Box>
      ) : (
        <ParlaySlip
          embedded
          site={site}
          excludeTracks={excludeTracks}
          drivers={byRank.map((d) => d.driver)}
          legs={legs}
          onChange={onLegsChange}
          showOdds={showOdds}
          model={model}
          chaos={chaos}
          uncertainty={uncertainty}
        />
      )}
      </Box>
    </Paper>
  );
};
